import { JSBridge, thsI18NJsBridge } from '@fs/jsbridge'
import wx from 'weixin-js-sdk'
import { isWeiXin, isDingtalk, isTHSApp, isTHSI18NApp, isPC } from '@fs/utils'
import {
  SESSION,
  UIN,
  SUB,
  RND_KEY,
  LOGINTIME_KEY,
  HAS_EXTEND_PROCESS_KEY,
} from '../constants/user'
/**
 * 严格校验中国护照号码
 * @param {string} passportNumber - 护照号码
 * @returns {boolean} - 是否符合中国护照号码格式
 */
export function validateChPassport(passportNumber: string): boolean {
  if (!passportNumber) return false

  // 去除空格并转换为大写
  const cleanedNumber = passportNumber.trim().toUpperCase()

  // 定义各种护照类型的正则表达式
  const patterns = [
    /^G\d{8}$/, // 旧版普通护照: G+8位数字
    /^E\d{8}$/, // 2012-2017年电子普通护照: E+8位数字
    /^E[A-HJ-NP-Z]\d{7}$/, // 2017年4月后电子普通护照: E+1位字母(不含I/O)+7位数字
  ]

  // 检查是否匹配任一模式
  return patterns.some((pattern) => pattern.test(cleanedNumber))
}

// 关闭页面
export const closePage = () => {
  if (JSBridge) {
    JSBridge.close()
  } else if (isWeiXin()) {
    wx.closeWindow()
  } else if (isDingtalk()) {
    import('dingtalk-jsapi').then((dd) => {
      dd.ready(function () {
        dd.biz.navigation.close({
          onSuccess: function () {},
          onFail: function () {},
        })
      })
    })
  } else if (isTHSApp()) {
    window.callNativeHandler('goback', { type: 'component' })
  } else if (isTHSI18NApp()) {
    thsI18NJsBridge.closeWebPage()
  } else {
    window.location.href = 'about:blank'
    window.close()
  }
}

/**
 * 获取指定长度的字符串(数字+字母)
 * @param n 长度
 * @returns 字符串
 */
export const getRndStr = (n: number, ishex = false): string => {
  let str = ''
  // 若是16进制，则取前6个字母
  const letterNum = ishex ? 6 : 26
  const total = ishex ? 22 : 62
  const upperNum = ishex ? 16 : 36
  for (let i = 0; i < n; i++) {
    const tmp = Math.floor(Math.random() * total)
    if (tmp < 10) {
      // 数字
      str += Math.floor(Math.random() * 10)
    } else if (tmp < upperNum) {
      // 大写字母
      str += String.fromCharCode(Math.floor(65 + Math.random() * letterNum))
    } else {
      // 小写字母
      str += String.fromCharCode(Math.floor(97 + Math.random() * letterNum))
    }
  }
  return str
}

export const isMobileTerminal = /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(
  navigator.userAgent
)

export const openUrl = (url = '', fileName = '') => {
  if (isPC()) {
    window.open(url)
  } else {
    const a = document.createElement('a')
    a.href = url
    if (fileName) {
      a.download = fileName
    }
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
}

const fitLen = function (num: number, length: number): string {
  const s = '0'.repeat(length) + String(num)
  return s.slice(s.length - length)
}

export interface HumanTimeOptions {
  minutesLen?: number
}

export const humanTime = function (
  msec: number,
  options: HumanTimeOptions = {}
): { days: string; hours: string; minutes: string; second: string } {
  const { minutesLen = 2 } = options

  const days = parseInt(String(msec / 1000 / 60 / 60 / 24), 10) //计算剩余的天数
  const hours = parseInt(String((msec / 1000 / 60 / 60) % 24), 10) //计算剩余的小时
  const minutes = parseInt(String((msec / 1000 / 60) % 60), 10) //计算剩余的分钟
  const second = parseInt(String((msec / 1000) % 60), 10) //计算剩余的秒
  return {
    days: fitLen(days, 2),
    hours: fitLen(hours, 2),
    minutes: fitLen(minutes, minutesLen),
    second: fitLen(second, 2),
  }
}

// 微信音频Audio兼容处理
export const wxAudioHack = (audioDom: HTMLAudioElement | null | undefined): void => {
  if (audioDom) {
    setTimeout(() => {
      audioDom.muted = true
      const playResult = audioDom.play()
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch((err: unknown) => console.log('自动播放err: ', err))
      }
      audioDom.muted = false
    }, 100)
  }
}

export interface DataURLtoBlobOptions {
  toFile?: boolean
  filename?: string
  type?: string
}

export const dataURLtoBlob = (
  dataurl: string,
  { toFile = false, filename = 'unknown', type = 'image/jpeg' }: DataURLtoBlobOptions = {}
): Blob | File => {
  // 获取到base64编码
  const arr = dataurl.split(',')
  // 将base64编码转为字符串
  const bstr = window.atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n) // 创建初始化为0的，包含length个元素的无符号整型数组
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  if (toFile) {
    return new File([u8arr], filename, {
      type,
    })
  }
  return new Blob([u8arr], {
    type,
  })
}

// 是否横屏
export const isLandscape = () => {
  const width = document.documentElement.clientWidth
  const height = document.documentElement.clientHeight
  return width >= height
}

// 是否竖屏
export const isPortrait = () => {
  const width = document.documentElement.clientWidth
  const height = document.documentElement.clientHeight
  return width < height
}

//获取客户端类型
const getPhonePlatform = () => {
  let _platform = 'h5'
  const isAndroid =
    navigator.userAgent.indexOf('Android') > -1 || navigator.userAgent.indexOf('Linux') > -1
  if (isAndroid) {
    _platform = 'android'
  }
  const isiOS = !!navigator.userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  if (isiOS) {
    _platform = 'ios'
  }
  return _platform
}

export const openPDF = (url = '', title = '', isOssFile = false) => {
  if (JSBridge) {
    JSBridge.openPDF({
      url: encodeURIComponent(url),
      title: title,
      type: isOssFile ? 1 : 0,
    })
  } else if (thsI18NJsBridge.isTHSI18NApp()) {
    thsI18NJsBridge.openPDF({
      url: url,
      title: title,
    })
  } else if (isTHSApp() && getPhonePlatform() === 'android') {
    window.callNativeHandler(
      'HXDownloadFile',
      JSON.stringify({
        url: url,
        filetype: 'pdf',
        title: title,
      }),
      () => {}
    )
  } else {
    openUrl(url, title)
  }
}

export function clearUserInfo() {
  localStorage.removeItem(SESSION)
  localStorage.removeItem(UIN)
  localStorage.removeItem(SUB)
  localStorage.removeItem(RND_KEY)
  localStorage.removeItem(LOGINTIME_KEY)
  localStorage.removeItem(HAS_EXTEND_PROCESS_KEY)
}
